/**
 * French text for the shared components, under the `ui.*` namespace.
 *
 * Must have exactly the same shape as `en.ts` — `src/__tests__/i18n.test.ts`
 * fails if the two drift apart.
 *
 * Punctuation follows the wording already in `apps/frontend`: a space before a
 * colon, none before a question mark.
 *
 * NOTE: drafted alongside the English (PRX-2112) and not yet reviewed by a
 * French speaker. Worth a second pass before this text reaches users.
 */
export const fr = {
  ui: {
    common: {
      cancel: 'Annuler',
      continue: 'Continuer',
      remove: 'Supprimer',
      revoke: 'Révoquer',
    },

    colorScheme: {
      switchToLight: 'Passer au mode clair',
      switchToDark: 'Passer au mode sombre',
    },

    password: {
      newLabel: 'Nouveau mot de passe',
      confirmLabel: 'Confirmer le nouveau mot de passe',
      /** @param min the minimum number of characters */
      hint: 'Au moins {min} caractères. Une courte phrase est facile à retenir et difficile à deviner.',
      submit: 'Définir le mot de passe',

      error: {
        /** @param min the minimum number of characters */
        tooShort: 'Utilisez au moins {min} caractères',
        /** @param max the maximum number of characters */
        tooLong: 'Utilisez au plus {max} caractères',
        mismatch: 'Les deux mots de passe ne correspondent pas',
      },
    },

    /*
     * Why the server refused to set the password, as opposed to `password.error`
     * above, which is what the form works out on its own before asking.
     */
    passwordReset: {
      error: {
        expired:
          'Cela a pris trop de temps, ou le lien a déjà été utilisé. Reconnectez-vous pour recommencer.',
        rejected: 'Ce mot de passe a été refusé. Veuillez en choisir un autre.',
        rateLimited: 'Trop de tentatives. Attendez une minute, puis réessayez.',
        server: 'Une erreur est survenue de notre côté. Veuillez réessayer.',
        network: 'Impossible de joindre le serveur. Vérifiez votre connexion et réessayez.',
      },
    },

    /*
     * Mot de passe oublié en libre-service (PRX-2033). Le parcours : demander un
     * code, le saisir, choisir un compte si l'adresse en correspond à plusieurs,
     * puis définir un mot de passe.
     */
    forgotPassword: {
      emailTitle: 'Mot de passe oublié?',
      emailHint: 'Saisissez votre adresse courriel et nous vous enverrons un code.',
      emailLabel: 'Adresse courriel',
      emailSubmit: 'Envoyer le code',

      codeTitle: 'Vérifiez vos courriels',
      /** @param email the address the code was sent to */
      codeHint: 'Si {email} est dans nos dossiers, nous y avons envoyé un code à 6 chiffres.',
      codeLabel: 'Code à six chiffres',
      codeSubmit: 'Continuer',
      resend: 'Renvoyer le code',
      /** @param seconds how long until the button comes back */
      resendIn: 'Renvoyer le code dans {seconds} s',
      resendExhausted:
        'Toujours pas de code? Vérifiez vos pourriels. Vous pourrez redemander dans quelques minutes.',
      back: 'Utiliser une autre adresse',

      accountTitle: 'Quel compte?',
      accountHint: 'Plusieurs comptes utilisent cette adresse. Choisissez celui à réinitialiser.',
      /** @param id the account's tenant number, when it has no name to show */
      accountFallback: 'Compte {id}',

      passwordTitle: 'Définir un nouveau mot de passe',

      error: {
        badCode: "Ce code n'a pas fonctionné. Vérifiez-le et réessayez, ou demandez-en un nouveau.",
        rateLimited: 'Trop de tentatives. Attendez une minute, puis réessayez.',
        server: 'Une erreur est survenue de notre côté. Veuillez réessayer.',
        network: 'Impossible de joindre le serveur. Vérifiez votre connexion et réessayez.',
      },
    },

    /*
     * L'étape du second facteur à la connexion : saisir le code, en demander un
     * autre, ou choisir une autre façon de le recevoir.
     */
    mfaVerify: {
      title: 'Vérification requise',
      subtitle: 'Entrez le code envoyé à votre appareil',
      codeLabel: 'Code de vérification',
      submit: 'Soumettre',
      trustDevice: 'Faire confiance à cet appareil pendant 30 jours',
      resend: "Vous n'avez pas reçu le code? Renvoyer",
      resending: 'Envoi en cours...',
      switchMethod: 'Utiliser une autre méthode',
      useBackupCode: 'Utiliser un code de secours',
      useAuthCode: 'Utiliser plutôt un code de vérification',
      backupCodeLabel: 'Code de secours',
      backupCodeHint:
        'Un des codes que vous avez enregistrés lors de la configuration de la vérification en deux étapes',

      method: {
        totp: "Application d'authentification",
        sms: 'Message texte',
        email: 'Courriel',
      },
    },

    /*
     * Codes de secours à usage unique en cas de perte du téléphone (PRX-2159).
     */
    backupCodes: {
      title: 'Enregistrez vos codes de secours',
      description:
        "Si vous perdez votre téléphone, connectez-vous avec l'un de ces codes. Chaque code ne fonctionne qu'une fois. Gardez-les en lieu sûr, comme un gestionnaire de mots de passe. Vous ne les reverrez plus.",
      listLabel: 'Codes de secours',
      copy: 'Copier les codes',
      copied: 'Copié',
      download: 'Télécharger les codes',
      fileName: 'codes-de-secours.txt',
      fileTitle: 'Codes de secours pour la vérification en deux étapes',
      fileNote: "Chaque code ne fonctionne qu'une fois. Gardez ce fichier en lieu sûr.",
      done: "J'ai enregistré mes codes",

      cardTitle: 'Codes de secours',
      cardDescription:
        'Utilisez un code de secours pour vous connecter si vous perdez votre téléphone.',
      /** @param count nombre de codes inutilisés */
      remaining: 'Aucun code restant | 1 code restant | {count} codes restants',
      lowWarning: 'Il vous reste peu de codes de secours. Créez-en de nouveaux.',
      regenerate: 'Créer de nouveaux codes',
      regenerateConfirm: {
        header: 'Créer de nouveaux codes de secours?',
        message: 'Vos anciens codes de secours ne fonctionneront plus.',
        accept: 'Créer de nouveaux codes',
      },
      regenerateFailed: 'Impossible de créer de nouveaux codes de secours. Réessayez.',
    },

    mfaSetup: {
      title: 'Configurer la vérification en deux étapes',
      subtitle:
        'Votre compte exige une deuxième étape pour se connecter. Choisissez comment recevoir vos codes.',
      totpTitle: "Application d'authentification",
      totpDescription:
        'Utilisez Google Authenticator, Authy ou 1Password. Fonctionne sans réseau cellulaire.',
      smsTitle: 'Message texte',
      smsDescription: 'Recevez un code par message texte',
      smsLink: "Vous ne pouvez pas utiliser d'application? Recevez les codes par texto",
      recommended: 'Recommandé',
      phoneLabel: 'Numéro de téléphone',
      phoneHint: "Incluez l'indicatif du pays, par exemple +15551234567",
      sendCode: 'Envoyer le code',
      scanPrompt: "Balayez ce code QR avec votre application d'authentification",
      manualKeyPrompt: 'Impossible de le balayer? Entrez cette clé :',
      smsCodePrompt: 'Entrez le code à 6 chiffres envoyé à votre téléphone',
      codeLabel: 'Code de vérification',
      submit: 'Vérifier et continuer',
      resend: 'Envoyer un nouveau code',
      useDifferentMethod: 'Utiliser une autre méthode',

      error: {
        invalidCode: "Ce code n'a pas fonctionné. Réessayez.",
        invalidPhone: "Entrez le numéro avec l'indicatif du pays, par exemple +15551234567.",
        expired: 'Le délai de configuration est écoulé. Connectez-vous de nouveau pour continuer.',
        locked: 'Trop de tentatives. Attendez quelques minutes, puis connectez-vous de nouveau.',
        signInAgain:
          "L'authentification multifacteur est configurée. Connectez-vous de nouveau pour continuer.",
        rateLimited: 'Trop de tentatives. Attendez une minute et réessayez.',
        network: 'Impossible de joindre le serveur. Vérifiez votre connexion et réessayez.',
        server: "Une erreur s'est produite. Réessayez.",
      },
    },

    mfa: {
      title: 'Authentification multifacteur',
      addMethod: 'Ajouter une méthode',
      authenticatorApp: "Application d'authentification",
      /** @param last4 last four digits of the phone number */
      smsMasked: 'SMS (•••• {last4})',
      preferredBadge: 'PRÉFÉRÉE',
      setPreferred: 'Définir comme préférée',

      empty: {
        title: "Aucune méthode d'authentification configurée",
        description:
          "Ajoutez une application d'authentification ou la vérification par SMS pour sécuriser votre compte",
      },

      removeConfirm: {
        header: "Supprimer la méthode d'authentification",
        message:
          'Cette méthode ne sera plus disponible pour la vérification de connexion. Continuer?',
      },

      notify: {
        preferredUpdated: 'Méthode préférée mise à jour',
        preferredFailed: 'Échec de la mise à jour de la méthode préférée',
        removed: "Méthode d'authentification supprimée",
        removeFailed: "Échec de la suppression de la méthode d'authentification",
        added: "Méthode d'authentification ajoutée",
      },

      enroll: {
        header: "Ajouter une méthode d'authentification",
        choosePrompt: 'Choisissez comment vérifier votre identité',
        recommendedBadge: 'RECOMMANDÉE',
        totpDescription: 'Utilisez Google Authenticator, Authy ou 1Password',
        smsTitle: 'Vérification par SMS',
        smsDescription: 'Recevez un code par message texte',
        phonePrompt: 'Saisissez votre numéro de téléphone pour recevoir les codes de vérification',
        phoneLabel: 'Numéro de téléphone',
        phonePlaceholder: '+1 (555) 123-4567',
        scanPrompt: "Numérisez ce code QR avec votre application d'authentification",
        manualKeyPrompt: 'Impossible de numériser? Saisissez cette clé manuellement :',
        totpCodeLabel: 'Saisissez le code à 6 chiffres de votre application',
        smsCodePrompt: 'Saisissez le code à 6 chiffres envoyé à votre téléphone',
        resendCode: 'Renvoyer le code',
        sendCode: 'Envoyer le code',
        verifyAndEnable: 'Vérifier et activer',
      },

      recommended: {
        bannerMessage: "Votre compte n'est pas protégé par l'authentification multifacteur.",
        bannerSuggestion:
          "Nous vous recommandons de configurer une application d'authentification.",
        bannerAction: "Configurer l'authentification",
        dialogTitle: 'Sécurisez votre compte',
        dialogBody:
          "L'authentification multifacteur ajoute une couche de protection supplémentaire. Nous vous recommandons de configurer une application d'authentification.",
        dialogSetup: 'Configurer maintenant',
        dialogLater: 'Me le rappeler plus tard',
      },
    },

    trustedDevices: {
      title: 'Appareils de confiance',
      revokeAll: 'Tout révoquer',
      empty:
        'Aucun appareil de confiance. Les appareils auxquels vous faites confiance lors de la vérification multifacteur apparaîtront ici.',
      unknownDevice: 'Appareil inconnu',
      thisDevice: 'Cet appareil',
      /** @param date already-formatted date */
      lastUsed: 'Dernière utilisation : {date}',
      /** @param date already-formatted date */
      expires: 'Expire le {date}',

      revokeAllConfirm: {
        header: 'Révoquer tous les appareils',
        message:
          'Tous les appareils devront effectuer la vérification multifacteur à la prochaine connexion. Continuer?',
      },

      revokeConfirm: {
        header: 'Révoquer un appareil de confiance',
        /** @param device the device's friendly name */
        message:
          'Révoquer la confiance pour {device}? Vous devrez vérifier votre identité de nouveau sur cet appareil.',
      },

      notify: {
        revoked: 'Appareil révoqué',
        revokeFailed: "Échec de la révocation de l'appareil",
        allRevoked: 'Tous les appareils ont été révoqués',
        revokeAllFailed: 'Échec de la révocation des appareils',
      },

      date: {
        today: "Aujourd'hui",
        yesterday: 'Hier',
      },
    },
  },
};
